/**
 * Centralized language configuration for the Mercury Marine PKP application.
 *
 * To add a new language:
 * 1. Add entry to LANGUAGE_CONFIG below
 * 2. Add Craft CMS site (Settings → Sites)
 * 3. Create translations/{code}/site.php and imports/ directory
 * 4. Import content via craft mercury/import-lang/* commands
 * 5. Update LanguageConfig.php if CSV uses new naming pattern
 */

export const LANGUAGE_CONFIG = {
  en: {
    name: 'English',
    code: 'en',
    craftLanguage: 'en',
    momentLocale: 'en',
    elementUILocale: 'en',
    currency: 'USD',
  },
  es: {
    name: 'Español (América Latina)',
    code: 'es',
    craftLanguage: 'es',
    momentLocale: 'es',
    elementUILocale: 'es',
    currency: 'USD',
  },
  zh: {
    name: '中文',
    code: 'zh',
    craftLanguage: 'zh',
    momentLocale: 'zh-cn',
    elementUILocale: 'zh-CN',
    currency: 'USD',
  },
  fr: {
    name: 'Français',
    code: 'fr',
    craftLanguage: 'fr',
    momentLocale: 'fr',
    elementUILocale: 'fr',
    currency: 'EUR',
  },
  it: {
    name: 'Italiano',
    code: 'it',
    craftLanguage: 'it',
    momentLocale: 'it',
    elementUILocale: 'it',
    currency: 'EUR',
  },
  'es-ES': {
    name: 'Español (Europa)',
    code: 'es-ES',
    craftLanguage: 'es-ES',
    momentLocale: 'es',
    elementUILocale: 'es',
    currency: 'EUR',
  },
  de: {
    name: 'Deutsch',
    code: 'de',
    craftLanguage: 'de',
    momentLocale: 'de',
    elementUILocale: 'de',
    currency: 'EUR',
  },
};

// Language name mappings for CSV imports
// Maps full language names to language codes
export const CSV_LANGUAGE_MAP = {
  'english': 'en',
  'spanish': 'es',
  'mandarin': 'zh',
  'chinese': 'zh',
  'french': 'fr',
  'italian': 'it',
  'german': 'de',
};

// Currency configurations
export const CURRENCY_CONFIG = {
  USD: {
    style: 'currency',
    currency: 'USD',
    useGrouping: true,
  },
  EUR: {
    style: 'currency',
    currency: 'EUR',
    useGrouping: true,
  },
};

/**
 * Element API routes use Craft site handles in the first URL segment (`api/<site>/…`).
 * i18n / LANGUAGE_CONFIG keys should match site handles (e.g. `es-ES`).
 * Translation routes (`api/<language>/translations`) use i18n keys — do not use this there.
 */
export function localeToCraftSiteHandle(locale) {
  if (!locale) {
    return 'en';
  }
  return locale;
}

/** Legacy or alternate handles that should map to the EU Spanish app locale (`es-ES`). */
const EU_SPANISH_SITE_HANDLES = new Set(['esEs', 'es-eu', 'es_EU', 'es-ES']);

/**
 * Map Craft site handle (from `/api/sites`) to LANGUAGE_CONFIG / i18n key.
 * Use for select option values so they match `preferredLanguage` and `i18n.language`.
 * Use {@link localeToCraftSiteHandle} for API URL segments and GraphQL site.
 */
export function craftSiteHandleToAppLocale(handle) {
  if (handle == null || handle === '') {
    return 'en';
  }
  const h = String(handle);
  if (EU_SPANISH_SITE_HANDLES.has(h)) {
    return 'es-ES';
  }
  return h;
}

// Helper to get all active language codes
export function getActiveLanguages() {
  return Object.keys(LANGUAGE_CONFIG);
}

// Helper to get currency config for a language
export function getCurrencyForLanguage(langCode) {
  const lang = LANGUAGE_CONFIG[langCode];
  return lang ? CURRENCY_CONFIG[lang.currency] : CURRENCY_CONFIG.USD;
}

export default LANGUAGE_CONFIG;

